import { Typography, Flex, Image, Tag } from 'antd';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { formatNumberToLocale, updateAttributesOfCdnImage } from '@admin-panel/utils';
import { ProductDTOType } from '../../../schemas/product';
import { ImageMask, useImageMask } from '@admin-panel/components';
import * as Styles from './styles';

function getCategoryNames(category: ProductDTOType['productType']): Array<string> {
  const names: Array<string> = [];
  let current = category;
  while (current) {
    names.unshift(current.name);
    current = current.parent;
  }
  return names;
}

const gradients = [
  'linear-gradient(45deg, #fff5d6 0%, #a29bfe 100%)',
  'linear-gradient(135deg, #aaf5e4 0%, #fdd5c4 100%)',
  'linear-gradient(120deg, #d1cdff 0%, #f0a291 100%)',
  'linear-gradient(45deg, #feb8d4 0%, #74b9ff 100%)',
  'linear-gradient(135deg, #fee5a7 0%, #c0f6f6 100%)',
  'linear-gradient(120deg, #a7dcff 0%, #feb8d4 100%)',
  'linear-gradient(45deg, #7ddbcf 0%, #fff5d6 100%)',
  'linear-gradient(135deg, #f0a291 0%, #d1cdff 100%)',
  'linear-gradient(120deg, #fdd5c4 0%, #aaf5e4 100%)',
  'linear-gradient(45deg, #c0f6f6 0%, #fee5a7 100%)',
  'linear-gradient(135deg, #74b9ff 0%, #feb8d4 100%)',
  'linear-gradient(120deg, #a29bfe 0%, #7ddbcf 100%)',
  'linear-gradient(45deg, #fee5a7 0%, #d1cdff 100%)',
  'linear-gradient(135deg, #fdd5c4 0%, #a7dcff 100%)',
  'linear-gradient(120deg, #aaf5e4 0%, #f0a291 100%)',
];

const getGradientByString = (str: string, variantId?: string) => {
  const hashString = `${str}-${variantId || 'default'}`;

  let hash = 0;
  for (let i = 0; i < hashString.length; i++) {
    const char = hashString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  const index = Math.abs(hash) % gradients.length;
  return gradients[index];
};

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const AnimatedTag = styled(Tag)<{ gradient: string }>`
  font-size: 11px;
  font-weight: 700;
  border-radius: 20px;
  background: ${props => props.gradient};
  background-size: 400% 400%;
  color: #171717;
  text-transform: uppercase;
  animation: ${gradientShift} 4s ease-in-out infinite;
`;

interface IProductCardProps {
  data: ProductDTOType;
  variant?: ProductDTOType['variants'][number];
}

const ProductCard = ({ data, variant }: IProductCardProps) => {
  const firstImage = variant?.images?.[0] || data.images?.[0];
  const firstVariant = variant || data.variants[0];

  const {
    isPreviewOpen,
    setIsPreviewOpen,
    previewCoords,
    handleMaskMouseEnter,
    handleMaskMouseLeave,
  } = useImageMask({
    magnificationFactor: 50,
  });

  const categoryNames = getCategoryNames(data.productType);

  return (
    <Styles.Wrapper
      styles={{
        body: {
          padding: 12,
        },
      }}
    >
      <Flex gap={16}>
        <Image
          width={128}
          height={192}
          onMouseEnter={handleMaskMouseEnter}
          onMouseLeave={handleMaskMouseLeave}
          style={{
            objectFit: 'cover',
            borderRadius: 8,
            flexShrink: 0,
            minWidth: 128,
            maxWidth: 128,
          }}
          src={updateAttributesOfCdnImage(firstImage?.url, {
            width: 360,
            quality: 100,
          })}
          preview={
            firstImage?.url
              ? {
                  mask: (
                    <ImageMask
                      fileUrl={updateAttributesOfCdnImage(firstImage?.url, {
                        width: 720,
                        quality: 100,
                      })}
                      magnificationFactor={50}
                      isPreviewOpen={isPreviewOpen}
                      setIsPreviewOpen={setIsPreviewOpen}
                      previewCoords={previewCoords}
                    />
                  ),
                  maskClassName: 'image-mask',
                }
              : false
          }
          fallback="https://placehold.co/96x144?text=No+Image"
        />
        <Flex vertical>
          <Typography.Paragraph
            strong
            style={{
              fontSize: 12,
              marginBottom: 8,
              textTransform: 'uppercase',
            }}
          >
            {data.brandName}
          </Typography.Paragraph>
          <Typography.Paragraph
            style={{
              fontSize: 14,
              marginBottom: 2,
            }}
          >
            {data.name}
          </Typography.Paragraph>
          <Typography.Paragraph
            strong
            style={{
              fontSize: 12,
              marginBottom: 8,
            }}
          >
            ₹{formatNumberToLocale(firstVariant.mrp)}
          </Typography.Paragraph>
          <Flex wrap gap={4} style={{ marginBottom: 8 }}>
            {categoryNames.map(cat => (
              <Tag
                key={cat}
                bordered={false}
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  borderRadius: 20,
                  background: '#F1F3F5',
                  color: '#171717',
                  textTransform: 'uppercase',
                }}
              >
                {cat}
              </Tag>
            ))}
            {firstVariant.aiTags?.map(aiTag => (
              <AnimatedTag
                key={aiTag}
                bordered={false}
                gradient={getGradientByString(aiTag, String(firstVariant.id))}
              >
                {aiTag}
              </AnimatedTag>
            ))}
          </Flex>
        </Flex>
      </Flex>
    </Styles.Wrapper>
  );
};

export default ProductCard;
